import { useEffect, useState, useRef, useCallback } from "react";

/**
 * useCountdown Hook
 * @param {Object} options - Configuration object
 * @param {number} options.initialTime - Countdown duration in seconds
 * @param {Function} options.onComplete - Callback when countdown reaches 0
 * @returns {Object} { timeLeft, start, stop, reset, isRunning, getElapsedTime }
 */
export function useCountdown({ initialTime, onComplete }) {
  const [timeLeft, setTimeLeft] = useState(initialTime);
  const [isRunning, setIsRunning] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const timerRef = useRef(null);
  const endTimeRef = useRef(null);
  const onCompleteRef = useRef(onComplete);

  // Update the callback ref when onComplete changes
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const updateTimeLeft = useCallback(() => {
    if (!endTimeRef.current) return;
    const remainingMs = endTimeRef.current - Date.now();
    const remainingSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    setTimeLeft(remainingSeconds);

    if (remainingSeconds <= 0) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      endTimeRef.current = null;
      setIsRunning(false);
      if (onCompleteRef.current) {
        // Fire on next tick so React state flushes first
        setTimeout(() => onCompleteRef.current(), 0);
      }
    }
  }, []);

  const start = useCallback((customTime = null) => {
    const timeToUse =
      typeof customTime === "number" ? customTime : initialTime;
    const normalizedTime = Math.max(0, Math.floor(timeToUse));

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const startedAt = Date.now();
    setStartTime(normalizedTime > 0 ? startedAt : null);
    endTimeRef.current = normalizedTime > 0
      ? startedAt + normalizedTime * 1000
      : null;
    setTimeLeft(normalizedTime);

    if (normalizedTime === 0) {
      setIsRunning(false);
      if (onCompleteRef.current) {
        setTimeout(() => onCompleteRef.current(), 0);
      }
      return;
    }

    setIsRunning(true);
    updateTimeLeft();
    timerRef.current = setInterval(updateTimeLeft, 250);
  }, [initialTime, updateTimeLeft]);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    endTimeRef.current = null;
    setIsRunning(false);
  }, []);

  const reset = useCallback((customTime = null) => {
    stop();
    const timeToUse =
      typeof customTime === "number" ? customTime : initialTime;
    const normalizedTime = Math.max(0, Math.floor(timeToUse));
    setTimeLeft(normalizedTime);
    setStartTime(null);
    endTimeRef.current = null;
  }, [initialTime, stop]);

  const getElapsedTime = useCallback(() => {
    if (!startTime) return 0;
    return Math.floor((Date.now() - startTime) / 1000);
  }, [startTime]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        updateTimeLeft();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, [updateTimeLeft]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  return {
    timeLeft,
    start,
    stop,
    reset,
    isRunning,
    getElapsedTime
  };
}